import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { InfoTip } from './InfoTip';
import s from './Field.module.css';
import { t } from '@app/i18n';

export type FieldSpan = 3 | 4 | 6 | 8 | 12;

const SPAN: Record<FieldSpan, string> = {
  3: s.c3,
  4: s.c4,
  6: s.c6,
  8: s.c8,
  12: s.c12,
};

export interface FieldGridProps {
  children: ReactNode;
  className?: string;
}

/** Grid de 12 colunas dos formulários (`.fgrid`). */
export function FieldGrid({ children, className }: FieldGridProps) {
  return <div className={[s.fgrid, className].filter(Boolean).join(' ')}>{children}</div>;
}

export interface FieldProps {
  label: string;
  /** Largura em colunas do grid de 12. Padrão: 6. */
  span?: FieldSpan;
  required?: boolean;
  /** Texto auxiliar abaixo do campo. */
  help?: ReactNode;
  /** Mensagem de erro. Presente = campo inválido. */
  error?: string | null;
  /** Explicação ⓘ ao lado do rótulo — obrigatória em campo de configuração (FR-132). */
  tip?: string;
  /** Rende o controle recebendo os ids/aria já ligados. */
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: true;
    'aria-required'?: true;
    className: string;
  }) => ReactNode;
  className?: string;
}

/**
 * Campo de formulário (`.fld`): rótulo ligado ao controle por `htmlFor`/`id`,
 * ajuda e erro anunciados por `aria-describedby`.
 */
export function Field({
  label,
  span = 6,
  required,
  help,
  error,
  tip,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const describedBy = [help ? helpId : null, error ? errId : null].filter(Boolean).join(' ');

  return (
    <div
      className={[s.fld, SPAN[span], error ? s.invalid : '', className].filter(Boolean).join(' ')}
    >
      {/*
        O `*` e o ⓘ ficam **fora** do `<label>`, como irmãos.

        Dentro dele, os dois entravam no `textContent` do rótulo — e é por
        `textContent` que `getByLabel` (Playwright e Testing Library) acha o
        campo, então `getByLabel('Senha', { exact: true })` não casava e o E2E
        precisava de regex. O nome **acessível** já estava certo (o `*` é
        `aria-hidden`), mas o do ⓘ não estaria: o `aria-label="Sobre Senha"` de
        um botão dentro do label entra na composição do nome do campo.
      */}
      <span className={s.flblRow}>
        <label className={s.flbl} htmlFor={id}>
          {label}
        </label>
        {required ? (
          <span className={s.req} aria-hidden="true">
            *
          </span>
        ) : null}
        {tip ? <InfoTip text={tip} label={t('common.ui.about', { label })} /> : null}
      </span>

      {children({
        id,
        className: s.fin,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(required ? { 'aria-required': true as const } : {}),
      })}

      {help ? (
        <span className={s.fhelp} id={helpId}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className={s.ferr} id={errId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** `true` = identificador técnico, renderizado em monoespaçada (FR-133). */
  mono?: boolean;
}

/** `<input>` com o traço do design (`.fin`). */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { mono, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={[className ?? s.fin, mono ? s.mono : ''].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
}

/** `<textarea>` com o traço do design (`.fin.fta`). */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { mono, className, rows = 4, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={[className ?? s.fin, s.fta, mono ? s.mono : ''].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});
